import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

// ✅ Get agent products - تحديث للتوافق مع كلا الطريقتين
export const fetchAgentProducts = createAsyncThunk(
  "agentProducts/fetchAgentProducts",
  async (params, thunkAPI) => {
    try {
      // ✅ دعم كلا الطريقتين: object أو agentId مباشرة
      let agentId, page, search, productType, status, append;
      
      if (typeof params === 'object' && params !== null) {
        // ✅ إذا كان object (من AgentProducts.jsx)
        ({ agentId, page = 1, search = "", productType = "", status = "", append = false } = params);
      } else {
        // ✅ إذا كان number/string فقط (من AgentProductsPopup)
        agentId = params;
        page = 1;
        search = "";
        productType = "";
        status = "";
        append = false;
      }

      console.log("📡 Fetching agent products:");
      console.log("  Agent ID:", agentId);
      console.log("  Page:", page);
      console.log("  Search:", search);
      console.log("  Product Type:", productType);
      console.log("  Status:", status);
      console.log("  Append:", append);

      const token = localStorage.getItem("accessToken");
      let url = `${API_URL}/agents/${agentId}/products/?page=${page}`;
      
      if (search) url += `&search=${search}`;
      if (productType) url += `&product_type=${productType}`;
      if (status) url += `&status=${status}`;

      console.log("🔗 Request URL:", url);

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      console.log("✅ Response received:", response.data);

      return {
        ...response.data,
        append,
      };
    } catch (error) {
      console.error("❌ Error fetching agent products:", error);
      console.error("Error response:", error.response?.data);
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Delete agent products
export const deleteAgentProducts = createAsyncThunk(
  "agentProducts/deleteAgentProducts",
  async ({ agentId, productIds }, thunkAPI) => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await axios.delete(
        `${API_URL}/agents/${agentId}/products/bulk-delete/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          data: { product_ids: productIds },
        }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Toggle agent active status
export const toggleAgentActive = createAsyncThunk(
  "agentProducts/toggleAgentActive",
  async ({ agentId, isActive }, thunkAPI) => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await axios.patch(
        `${API_URL}/agents/${agentId}/toggle-active/`,
        { is_active: isActive },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

const agentProductsSlice = createSlice({
  name: "agentProducts",
  initialState: {
    products: [],
    agent: null,
    count: 0,
    next: null,
    previous: null,
    loading: false,
    error: null,
    deleteLoading: false,
    toggleLoading: false,
    hasMore: true,
    currentPage: 1,
    totalPages: 1,
  },
  reducers: {
    clearProducts: (state) => {
      state.products = [];
      state.agent = null;
      state.count = 0;
      state.next = null;
      state.previous = null;
      state.error = null;
      state.hasMore = true;
      state.currentPage = 1;
      state.totalPages = 1;
    },
    resetPagination: (state) => {
      state.products = [];
      state.currentPage = 1;
      state.hasMore = true;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch products
      .addCase(fetchAgentProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAgentProducts.fulfilled, (state, action) => {
        state.loading = false;
        
        console.log("✅ Redux state updated with:", action.payload);
        
        // ✅ إذا كان append = true، نضيف المنتجات للقائمة الموجودة
        if (action.payload.append) {
          state.products = [...state.products, ...action.payload.results.products];
        } else {
          // ✅ إذا كان append = false، نستبدل القائمة
          state.products = action.payload.results.products;
        }
        
        state.agent = action.payload.results.agent;
        state.count = action.payload.count;
        state.next = action.payload.next;
        state.previous = action.payload.previous;
        state.hasMore = action.payload.next !== null;
        
        // ✅ حساب الصفحة الحالية والإجمالي
        const pageSize = 20;
        state.currentPage = Math.floor(state.products.length / pageSize) || 1;
        state.totalPages = Math.ceil(action.payload.count / pageSize);
        
        console.log("📊 Products count:", state.products.length);
        console.log("📄 Current page:", state.currentPage);
        console.log("📄 Total pages:", state.totalPages);
      })
      .addCase(fetchAgentProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.hasMore = false;
        console.error("❌ Redux state error:", action.payload);
      })
      // Delete products
      .addCase(deleteAgentProducts.pending, (state) => {
        state.deleteLoading = true;
      })
      .addCase(deleteAgentProducts.fulfilled, (state) => {
        state.deleteLoading = false;
      })
      .addCase(deleteAgentProducts.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
      })
      // Toggle active
      .addCase(toggleAgentActive.pending, (state) => {
        state.toggleLoading = true;
      })
      .addCase(toggleAgentActive.fulfilled, (state, action) => {
        state.toggleLoading = false;
        if (state.agent) {
          state.agent.is_active = action.payload.is_active;
        }
      })
      .addCase(toggleAgentActive.rejected, (state, action) => {
        state.toggleLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearProducts, resetPagination } = agentProductsSlice.actions;
export default agentProductsSlice.reducer;